import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet} from '@angular/router';
import {filter, map, mergeMap, takeUntil, tap} from 'rxjs/operators';
import {BehaviorSubject, of, timer} from 'rxjs';
import {BreakpointObserver} from '@angular/cdk/layout';
import {TitleService} from './services/title.service';
import {SwUpdate, VersionReadyEvent} from '@angular/service-worker';
import {NGXLogger} from 'ngx-logger';
import {UntilDestroy} from '@ngneat/until-destroy';
import {CustomLogMonitor} from './shared/custom-log-monitor';
import {SupportChatService} from './services/support-chat.service';
import {StatusBannerService} from './services/status-banner.service';
import {SimpleDialogComponent} from './components/simple-dialog/simple-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {MatSidenav} from '@angular/material/sidenav';
import {MenuService} from './services/menu.service';
import {MatIconRegistry} from '@angular/material/icon';
import {DomSanitizer} from '@angular/platform-browser';
import {AuthService} from './services/auth.service';
import {COMPANY_TYPE} from '../environments/environment';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {
  private title = 'Atestado.me';
  private navigationStart$;
  private navigationEnd$;
  private updateDialog;
  public loading$ = new BehaviorSubject<boolean>(false);
  public renderChatButton = false;
  public companyType = COMPANY_TYPE;
  public menu: boolean;

  @ViewChild(RouterOutlet) public outlet: RouterOutlet;
  @ViewChild('sidenav') sidenav: MatSidenav;

  constructor(
    public supportChatService: SupportChatService,
    public statusBannerService: StatusBannerService,
    public authService: AuthService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private titleService: TitleService,
    private swUpdate: SwUpdate,
    private logger: NGXLogger,
    private dialog: MatDialog,
    private menuService: MenuService,
    private domSanitizer: DomSanitizer,
    private matIconRegistry: MatIconRegistry
  ) {
    // Send warn/error messages to Sentry
    this.logger.registerMonitor(new CustomLogMonitor());
    this.menuService.open
      .subscribe(() => this.sidenav.open());

    this.matIconRegistry.addSvgIcon(
      `stethoscope`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/stethoscope.svg')
    );
  }

  getTitle(state, parent): string[] {
    const data = [];
    if (parent?.snapshot?.data?.title) {
      data.push(parent.snapshot.data.title);
    }

    if (state && parent) {
      data.push(... this.getTitle(state, state.firstChild(parent)));
    }
    return data;
  }

  ngOnInit(): void {
    this.updateDetection();

    this.navigationEnd$ = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError));

    this.navigationStart$ = this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        map(() => timer(100)),
        takeUntil(this.navigationEnd$)
      )
      .subscribe(() => {
        this.loading$.next(true);
      });

    this.navigationEnd$.subscribe(() => {
      this.loading$.next(false);
      this.titleService.title = this.getTitle(this.router.routerState, this.router.routerState.root).join(' | ');
    });

    this.supportChatService.init();
  }

  ngAfterViewInit(): void {
    this.outlet.activateEvents.subscribe((component) => {
      // render chat button when child route is active (to decide color theme)
      this.renderChatButton = this.outlet.isActivated;
    });
  }

  updateDetection(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.pipe(
        tap((evt) => {
          console.log('Evt', evt.type);
        }),
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        map(evt => ({
          type: 'UPDATE_AVAILABLE',
          current: evt.currentVersion,
          available: evt.latestVersion,
        })),
        map(() => this.router.url === '/'),
        mergeMap((homeScreen) => {
          if (homeScreen) {
            return of(true);
          } else if (!this.updateDialog) {
            this.updateDialog = SimpleDialogComponent.open(this.dialog, {
              title: 'Nova versão do app',
              content: 'Precisamos atualizar a página para acessar a versão mais nova do app',
              okButton: 'OK',
              disableClose: true
            }).afterClosed();
          }
          return this.updateDialog;
        }))
        .subscribe(() => {
          this.forceUpdate();
        });

      // Force reload if an error is detected
      this.swUpdate.unrecoverable.subscribe(() => {
        this.forceUpdate();
      });
    }
  }

  forceUpdate(): void {
    window.location.reload();
  }

  public onSupportLauncherClick(): void {
    // Global launcher is always a neutral open; reset any screen-set routing context.
    this.supportChatService.setSupportContext('');
  }

  public menuEdit(): void {
    alert('menuEdit');
  }

  public signOut(): void {
    alert('Sign Out');
  }
}
